/**
 * Post visibility options.
 * - public: anyone can view
 * - logged_in: only authenticated users
 * - private: only the author
 * - listed: only author + users in allowedViewers (by email)
 */
export const VISIBILITY = {
  PUBLIC: 'public',
  LOGGED_IN: 'logged_in',
  PRIVATE: 'private',
  LISTED: 'listed',
};

export const VISIBILITY_ORDER = [VISIBILITY.PUBLIC, VISIBILITY.LOGGED_IN, VISIBILITY.LISTED, VISIBILITY.PRIVATE];

/**
 * Normalize visibility from API (handles snake_case or alternate keys).
 */
export function normalizeVisibility(post) {
  if (!post) return post;
  if (post.visibility && ['public', 'private', 'logged_in', 'listed'].includes(post.visibility)) {
    return post;
  }
  if (post.visibility_private === true) return { ...post, visibility: 'private' };
  if (post.visibility_logged_in === true) return { ...post, visibility: 'logged_in' };
  if (post.visibility_listed === true) return { ...post, visibility: 'listed' };
  return { ...post, visibility: post.visibility || 'public' };
}

/**
 * Whether the current user can view a post based on visibility and allowedViewers.
 */
export function canViewPost(post, user, isAuthenticated) {
  if (post == null) return false;
  const p = normalizeVisibility(post);
  if (p == null) return false;
  const visibility = p.visibility || 'public';
  const authorId = p.author?._id || p.author?.id || p.authorId;
  const isAuthor = user && (user._id === authorId || user.id === authorId || user.email === p.author?.email);

  if (isAuthor) return true;
  if (visibility === 'public') return true;
  if (visibility === 'logged_in') return !!isAuthenticated;
  if (visibility === 'private') return false;
  if (visibility === 'listed') {
    const allowed = p.allowedViewers ?? p.allowed_viewers ?? [];
    const emails = Array.isArray(allowed) ? allowed : (allowed && allowed.split ? allowed.split(',').map(e => e.trim()) : []);
    return isAuthenticated && user && emails.some(email => email && (user.email || '').toLowerCase() === email.toLowerCase());
  }
  return true;
}

/**
 * Filter posts for the list based on visibility and optional list filter.
 * listFilter: 'all' | 'mine' | 'public'
 */
export function filterPostsForList(posts, user, isAuthenticated, listFilter = 'all') {
  const normalized = (Array.isArray(posts) ? posts : [])
    .filter((p) => p != null)
    .map(normalizeVisibility)
    .filter((p) => p != null);
  const visible = normalized.filter((post) => canViewPost(post, user, isAuthenticated));
  if (listFilter === 'mine' && user) {
    return visible.filter(post => {
      const authorId = post.author?._id || post.author?.id || post.authorId;
      return user._id === authorId || user.id === authorId || user.email === post.author?.email;
    });
  }
  if (listFilter === 'public') {
    return visible.filter(post => (post.visibility || 'public') === 'public');
  }
  return visible;
}
